/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Adam (Tuannd@rsupport.com)
 *
 * @description
 *
 * @created at Mon Sep 23 2024
 **/

import { useEffect } from "react";

import { useEventHandlers } from "@rfice/eventhandler";
import { useStompClient } from "@/frameworks/libraries/network";
import { Configure } from "@/frameworks/libraries/utilities";

import { useMeeting } from "@/frameworks/core/API";
import { useConferenceCommon } from "@/frameworks/core/hooks";
import { EventID } from "@/frameworks/core/commons";
import useWakeLock from "@/frameworks/core/hooks/useWakeLock";

export default function ConferenceProvider() {
	const meetingAPI = useMeeting();
	const { startWakeLock } = useWakeLock();
	const {
		actions: { setConferenceInfo, setCurrentConference }
	} = useConferenceCommon();
	const conferenceInfo = useConferenceCommon().renderer.conferenceInfo();

	useEffect(() => {
		if (Configure.currentConference) {
			setCurrentConference(Configure.currentConference);
		}
	}, []);

	useStompClient(
		{
			async onMessageReceived(msg) {
				switch (msg.type) {
					case "conferenceParticipated": {
						const payload = msg.payload as PushServices.Conference.ConferenceParticipated.Data;
						const conferenceDetail = await meetingAPI.conference.detail({
							conferenceId: payload.conferenceId
						});
						if (conferenceDetail) {
							setConferenceInfo({
								conferenceId: conferenceDetail?.id || "",
								participantCount:
									conferenceDetail?.participants?.filter(
										(participant: { status: string }) => participant.status === "JOINED"
									)?.length || 0,
								totalUserCount: conferenceDetail?.participants?.length || 0,
								privateMeeting: Boolean(conferenceDetail?.privateMeeting),
								startTime: conferenceDetail?.startTime || 0,
								title: conferenceDetail?.title || "",
								sceneLevel: conferenceDetail?.spaceScene?.sceneLevel || "",
								sceneTypeNumber: conferenceDetail?.spaceScene?.sceneType?.typeNumber || 0
							});
						}
						break;
					}
					case "conferenceLeft": {
						const payload = msg.payload as PushServices.Conference.ConferenceLeft.Data;
						const conferenceDetail = await meetingAPI.conference.detail({
							conferenceId: payload.conferenceId
						});
						if (conferenceDetail) {
							setConferenceInfo({
								conferenceId: conferenceDetail?.id || "",
								participantCount:
									conferenceDetail?.participants?.filter(
										(participant: { status: string }) => participant.status === "JOINED"
									)?.length || 0,
								totalUserCount: conferenceDetail?.participants?.length || 0,
								privateMeeting: Boolean(conferenceDetail?.privateMeeting),
								startTime: conferenceDetail?.startTime || 0,
								title: conferenceDetail?.title || "",
								sceneLevel: conferenceDetail?.spaceScene?.sceneLevel || "",
								sceneTypeNumber: conferenceDetail?.spaceScene?.sceneType?.typeNumber || 0
							});
						}
						break;
					}
					case "conferenceInfoUpdated": {
						const payload = msg.payload as PushServices.Conference.conferenceInfoUpdated.Data;
						setConferenceInfo({
							title: payload.title || "",
							conferenceId: conferenceInfo?.conferenceId || "",
							participantCount: conferenceInfo?.participantCount || 0,
							totalUserCount: conferenceInfo?.totalUserCount || 0,
							privateMeeting: Boolean(conferenceInfo?.privateMeeting),
							startTime: conferenceInfo?.startTime || 0,
							sceneLevel: conferenceInfo?.sceneLevel || "",
							sceneTypeNumber: conferenceInfo?.sceneTypeNumber || 0
						});

						break;
					}
				}
			}
		},
		[]
	);

	useEventHandlers({
		[EventID.onUnityMessage](message: Message.Frame) {
			switch (message.type) {
				case "enterSpace": {
					const payload = message.payload;
					if (payload.meetingType === "CONFERENCE") {
						startWakeLock();
					}
				}
			}
		}
	});

	return null;
}
